import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { X, Upload, Save } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_META } from "./Marketplace";
import FeeBreakdown from "@/components/FeeBreakdown";

const CONDITION_OPTIONS = [
  { value: "new", label: "New — factory sealed / tags on", help: "Never opened, never used, still in original packaging or clothing with tags attached." },
  { value: "like_new", label: "Used — like new", help: "Opened but never used or worn. No signs of wear at all." },
  { value: "good", label: "Used — good", help: "Used with minor wear. Fully functional and cared for." },
  { value: "fair", label: "Used — fair", help: "Visible wear, scratches, or blemishes. Works as intended." },
  { value: "for_parts", label: "For parts / not working", help: "Non-functional or heavily damaged. Buyer intends to repair or salvage." },
];

const DELIVERY_OPTIONS = [
  { value: "pickup", label: "Pickup only" },
  { value: "shipping", label: "Ship only" },
  { value: "both", label: "Pickup or ship" },
];

const MAX_PHOTOS = 5;

async function resizeImageToDataUrl(file, maxDim = 1400, quality = 0.8) {
  const buf = await file.arrayBuffer();
  const blob = new Blob([buf], { type: file.type });
  const bmp = await createImageBitmap(blob);
  const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function MarketplaceSell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { lid } = useParams(); // edit mode when :lid is present
  const isEdit = !!lid;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priceDollars, setPriceDollars] = useState("");
  const [category, setCategory] = useState("other");
  const [condition, setCondition] = useState("good");
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);  // optional: up to 3 video URLs
  const [city, setCity] = useState(user?.city || "");
  const [state, setState] = useState(user?.state || "");
  const [zip, setZip] = useState(user?.zip || user?.business_zip || "");
  const [negotiable, setNegotiable] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState("pickup");
  const [shippingDollars, setShippingDollars] = useState("");
  // Auction fields — only used when listingType === "auction"
  const [listingType, setListingType] = useState("fixed");
  const [startingBidDollars, setStartingBidDollars] = useState("");
  const [reserveDollars, setReserveDollars] = useState("");
  const [buyNowDollars, setBuyNowDollars] = useState("");
  const [durationHours, setDurationHours] = useState("72"); // default 3 days
  // Shipping calculator fields — required when delivery includes shipping
  const [weightOz, setWeightOz] = useState("");
  const [lengthIn, setLengthIn] = useState("");
  const [widthIn, setWidthIn] = useState("");
  const [heightIn, setHeightIn] = useState("");
  // Fixed-price only: accept private offers
  const [acceptsOffers, setAcceptsOffers] = useState(false);
  // Artist merch attribution — the user may own one or more artist profiles;
  // we let them attach this listing to a specific artist page so it shows in
  // that artist's "Merch" strip.
  const [myArtists, setMyArtists] = useState([]);
  const [artistId, setArtistId] = useState("");
  const [saving, setSaving] = useState(false);
  const [kyc, setKyc] = useState(null);

  // Fetch KYC status once so we can gate paid listings and show a banner.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/marketplace/seller/status");
        setKyc(data);
      } catch {
        /* status unavailable → treat as not-onboarded */
      }
      try {
        const { data } = await api.get("/me/artists");
        setMyArtists(data?.items || []);
      } catch {
        /* user has no artist profiles */
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await api.get(`/marketplace/listings/${lid}`);
        const l = r.data;
        setTitle(l.title || "");
        setDescription(l.description || "");
        setPriceDollars(l.price_cents ? (l.price_cents / 100).toString() : "");
        setCategory(l.category || "other");
        setCondition(l.condition || "good");
        setPhotos(l.photos || []);
        setVideos(l.videos || []);
        setCity(l.city || ""); setState(l.state || ""); setZip(l.zip || "");
        setNegotiable(!!l.negotiable);
        setDeliveryMode(l.delivery_mode || "pickup");
        setShippingDollars(l.shipping_cents ? (l.shipping_cents / 100).toString() : "");
        setListingType(l.listing_type || "fixed");
        setStartingBidDollars(l.starting_bid_cents ? (l.starting_bid_cents / 100).toString() : "");
        setReserveDollars(l.reserve_cents ? (l.reserve_cents / 100).toString() : "");
        setBuyNowDollars(l.buy_now_cents ? (l.buy_now_cents / 100).toString() : "");
        setDurationHours(l.duration_seconds ? Math.round(l.duration_seconds / 3600).toString() : "72");
        setWeightOz(l.weight_oz ? l.weight_oz.toString() : "");
        setLengthIn(l.length_in ? l.length_in.toString() : "");
        setWidthIn(l.width_in ? l.width_in.toString() : "");
        setHeightIn(l.height_in ? l.height_in.toString() : "");
        setAcceptsOffers(!!l.accepts_offers);
        setArtistId(l.artist_id || "");
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [lid, isEdit]);

  const addPhotos = async (files) => {
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) { toast.error(`Max ${MAX_PHOTOS} photos`); return; }
    const chunk = Array.from(files).slice(0, remaining);
    const newDataUrls = [];
    for (const f of chunk) {
      if (!f.type.startsWith("image/")) continue;
      try {
        const dataUrl = await resizeImageToDataUrl(f);
        newDataUrls.push(dataUrl);
      } catch (e) { toast.error("Couldn't process an image."); }
    }
    setPhotos(prev => [...prev, ...newDataUrls].slice(0, MAX_PHOTOS));
  };

  const removePhoto = (i) => setPhotos(prev => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (title.trim().length < 3) return toast.error("Title is required.");
    if (description.trim().length < 3) return toast.error("Description is required.");
    const shippingEnabled = (deliveryMode === "shipping" || deliveryMode === "both");
    // Shipping validation
    if (shippingEnabled) {
      const w = parseFloat(weightOz || "0");
      if (!w || w <= 0) return toast.error("Enter package weight (oz) when offering shipping.");
    }

    // Branch on listing type
    let payload;
    if (listingType === "auction") {
      const startCents = Math.round(parseFloat(startingBidDollars || "0") * 100);
      if (startCents < 100) return toast.error("Starting bid must be at least $1.00");
      const reserveCents = reserveDollars ? Math.round(parseFloat(reserveDollars) * 100) : null;
      if (reserveCents !== null && reserveCents < startCents) return toast.error("Reserve must be ≥ starting bid.");
      const buyNowCents = buyNowDollars ? Math.round(parseFloat(buyNowDollars) * 100) : null;
      if (buyNowCents !== null && buyNowCents <= startCents) return toast.error("Buy-now must be > starting bid.");
      const durHours = parseInt(durationHours || "0", 10);
      if (durHours < 1 || durHours > 336) return toast.error("Duration must be 1 hour to 14 days (336h).");
      // KYC gate (Stripe seller onboarding required for auctions).
      if (kyc && !kyc.can_list_paid) {
        toast.error("Complete Stripe seller onboarding first — redirecting…");
        nav("/marketplace/onboard");
        return;
      }
      payload = {
        listing_type: "auction",
        title: title.trim(),
        description: description.trim(),
        price_cents: startCents,   // reused as list-price surrogate on backend
        category, condition, photos, videos,
        city: city.trim(), state: state.trim(), zip: zip.trim(),
        negotiable: false,
        delivery_mode: deliveryMode,
        shipping_cents: shippingEnabled ? Math.round(parseFloat(shippingDollars || "0") * 100) : 0,
        weight_oz: shippingEnabled ? parseFloat(weightOz) : null,
        length_in: (shippingEnabled && lengthIn) ? parseFloat(lengthIn) : null,
        width_in: (shippingEnabled && widthIn) ? parseFloat(widthIn) : null,
        height_in: (shippingEnabled && heightIn) ? parseFloat(heightIn) : null,
        starting_bid_cents: startCents,
        reserve_cents: reserveCents,
        buy_now_cents: buyNowCents,
        duration_seconds: durHours * 3600,
        artist_id: artistId || null,
      };
    } else {
      const cents = Math.round(parseFloat(priceDollars || "0") * 100);
      if (Number.isNaN(cents) || cents < 0) return toast.error("Enter a valid price.");
      // KYC gate — paid listings require Stripe Connect onboarding.
      if (cents > 0 && kyc && !kyc.can_list_paid) {
        toast.error("Complete Stripe seller onboarding first — redirecting…");
        nav("/marketplace/onboard");
        return;
      }
      payload = {
        listing_type: "fixed",
        title: title.trim(),
        description: description.trim(),
        price_cents: cents,
        category, condition, photos, videos,
        city: city.trim(), state: state.trim(), zip: zip.trim(),
        negotiable, delivery_mode: deliveryMode,
        shipping_cents: shippingEnabled ? Math.round(parseFloat(shippingDollars || "0") * 100) : 0,
        weight_oz: shippingEnabled ? parseFloat(weightOz) : null,
        length_in: (shippingEnabled && lengthIn) ? parseFloat(lengthIn) : null,
        width_in: (shippingEnabled && widthIn) ? parseFloat(widthIn) : null,
        height_in: (shippingEnabled && heightIn) ? parseFloat(heightIn) : null,
        accepts_offers: acceptsOffers,
        artist_id: artistId || null,
      };
    }

    setSaving(true);
    try {
      if (isEdit) {
        const r = await api.patch(`/marketplace/listings/${lid}`, payload);
        toast.success("Listing updated");
        nav(`/marketplace/listings/${r.data.listing.id}`);
      } else {
        const r = await api.post(`/marketplace/listings`, payload);
        toast.success(listingType === "auction" ? "Auction posted" : "Listing posted");
        nav(`/marketplace/listings/${r.data.listing.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!user) {
    return <div className="max-w-lg mx-auto py-20 px-6 text-center text-gray-600">Sign in to sell an item.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
      <h1 className="font-display text-3xl">{isEdit ? "Edit listing" : "Sell an item"}</h1>
      <p className="mt-2 text-sm text-gray-600">Photos help — buyers scan images first, price second.</p>
      <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="mp-policy-notice">
        <strong>Prohibited items:</strong> firearms &amp; ammunition · drugs &amp; controlled substances · human body parts or organs · adult sexual content · escort or trafficking services. Listings that violate these rules are auto-rejected and repeat offenders are removed from the platform.
      </div>

      {kyc && !kyc.can_list_paid && (
        <div
          className="mt-3 rounded-lg border border-[#F0C9C4] bg-[#F9E4E1] px-4 py-3 text-sm text-[#1B1614] flex items-start gap-3"
          data-testid="mp-kyc-banner"
        >
          <div className="w-8 h-8 rounded-lg bg-[#B31E1E] flex items-center justify-center shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div className="flex-1">
            <div className="font-semibold">Seller onboarding required for paid listings</div>
            <div className="text-[13px] text-[#3E2A26] mt-0.5">
              Free / pickup-only listings can be posted anytime. To sell for money, complete Stripe&apos;s identity verification once — payouts land in your bank automatically.
            </div>
          </div>
          <Button
            size="sm"
            className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white font-semibold shrink-0"
            onClick={() => nav("/marketplace/onboard")}
            data-testid="mp-kyc-onboard-cta"
          >
            Start onboarding
          </Button>
        </div>
      )}

      <Card className="rounded-md p-6 mt-6 space-y-4">
        {/* Listing type toggle — Fixed price OR Timed auction. Editing an
            existing listing keeps the toggle visible but non-functional (the
            backend rejects changing type after creation). */}
        <div>
          <Label className="text-xs">Listing type</Label>
          <div className="mt-1.5 inline-flex rounded-lg border border-[#EAE3DE] bg-[#FAF5F2] p-1" role="tablist" data-testid="mp-listing-type-toggle">
            {[
              { v: "fixed", label: "Fixed price" },
              { v: "auction", label: "Timed auction" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                disabled={isEdit && listingType !== opt.v}
                onClick={() => setListingType(opt.v)}
                data-testid={`mp-listing-type-${opt.v}`}
                className={`px-4 py-1.5 text-[13px] font-semibold rounded-md transition-colors ${
                  listingType === opt.v
                    ? "bg-white text-[#1B1614] shadow-sm"
                    : "text-[#8B7B73] hover:text-[#1B1614]"
                } ${isEdit && listingType !== opt.v ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-[#8B7B73]">
            {listingType === "auction"
              ? "Buyers bid over a set duration. Highest bidder at close wins and is auto-charged. Anti-sniping extends the timer by 2 minutes on any last-minute bid."
              : "Buyers pay your listed price via Buy Now — or send a private offer if you enable offers."}
          </p>
        </div>

        <div>
          <Label className="text-xs">Photos ({photos.length}/{MAX_PHOTOS})</Label>
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
            {photos.map((p, i) => (
              <div key={`photo-${i}-${p.slice(-16)}`} className="relative aspect-square rounded-md overflow-hidden border border-border">
                <img src={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 shadow"
                  data-testid={`remove-photo-${i}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <label className="aspect-square rounded-md border border-dashed border-border flex items-center justify-center cursor-pointer text-gray-500 hover:border-[#B31E1E] hover:text-[#B31E1E]" data-testid="add-photo-tile">
                <Upload size={20} />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => addPhotos(e.target.files)}
                />
              </label>
            )}
          </div>
        </div>

        {/* Videos — optional. Paste up to 3 URLs (YouTube / Vimeo / direct
            MP4). Shows the item in action or highlights defects — big
            trust lever, especially on auctions. */}
        <div data-testid="mp-videos-section">
          <Label className="text-xs">
            Videos ({videos.length}/3) <span className="font-normal text-[#8B7B73]">— optional, shows item working / defects</span>
          </Label>
          <div className="mt-2 space-y-2">
            {videos.map((v, i) => (
              <div key={`video-${i}`} className="flex gap-2" data-testid={`mp-video-row-${i}`}>
                <Input
                  value={v}
                  onChange={(e) => setVideos(videos.map((old, idx) => idx === i ? e.target.value : old))}
                  placeholder="https://youtube.com/watch?v=… or direct .mp4 URL"
                  className="rounded-sm flex-1 font-mono text-xs"
                  data-testid={`mp-video-input-${i}`}
                />
                <button
                  type="button"
                  onClick={() => setVideos(videos.filter((_, idx) => idx !== i))}
                  className="text-red-600 hover:bg-red-50 px-2 rounded-sm"
                  data-testid={`mp-video-remove-${i}`}
                  aria-label="Remove video"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {videos.length < 3 && (
              <button
                type="button"
                onClick={() => setVideos([...videos, ""])}
                className="w-full py-2 rounded-md border border-dashed border-border text-xs text-[#5C4E48] hover:text-[#B31E1E] hover:border-[#B31E1E] transition-colors"
                data-testid="mp-video-add"
              >
                + Add video URL
              </button>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="mp-title" className="text-xs">Title</Label>
          <Input id="mp-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. IKEA Malm 6-drawer dresser" data-testid="mp-title-input" className="rounded-sm mt-1" />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="mp-price" className="text-xs">{listingType === "auction" ? "Starting bid (USD)" : "Price (USD)"}</Label>
            <Input
              id="mp-price"
              type="number" min={0} step="0.01"
              value={listingType === "auction" ? startingBidDollars : priceDollars}
              onChange={(e) => (listingType === "auction" ? setStartingBidDollars(e.target.value) : setPriceDollars(e.target.value))}
              data-testid="mp-price-input"
              className="rounded-sm mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="mp-category-input"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Condition</Label>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="mp-condition-input"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONDITION_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="mt-1.5 text-[11px] text-[#8B7B73] leading-snug" data-testid="mp-condition-help">
              {CONDITION_OPTIONS.find((c) => c.value === condition)?.help}
            </p>
          </div>
        </div>

        {/* Auction-specific fields */}
        {listingType === "auction" && (
          <div className="rounded-lg border border-[#EAE3DE] bg-[#FAF5F2] p-4 space-y-3" data-testid="mp-auction-fields">
            <div className="text-xs font-mono uppercase tracking-wider text-[#8B7B73]">Auction settings</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="mp-reserve" className="text-xs">Reserve price (optional)</Label>
                <Input id="mp-reserve" type="number" min={0} step="0.01" value={reserveDollars} onChange={(e) => setReserveDollars(e.target.value)} placeholder="Not disclosed to bidders" className="rounded-sm mt-1" data-testid="mp-reserve-input" />
                <p className="text-[10px] text-[#8B7B73] mt-1">Minimum you&apos;ll accept. Auction closes without a sale if unmet.</p>
              </div>
              <div>
                <Label htmlFor="mp-buynow" className="text-xs">Buy Now price (optional)</Label>
                <Input id="mp-buynow" type="number" min={0} step="0.01" value={buyNowDollars} onChange={(e) => setBuyNowDollars(e.target.value)} placeholder="Skip the wait" className="rounded-sm mt-1" data-testid="mp-buynow-input" />
                <p className="text-[10px] text-[#8B7B73] mt-1">Optional — lets a buyer end the auction instantly.</p>
              </div>
              <div>
                <Label htmlFor="mp-duration" className="text-xs">Duration (hours)</Label>
                <Input id="mp-duration" type="number" min={1} max={336} step="1" value={durationHours} onChange={(e) => setDurationHours(e.target.value)} className="rounded-sm mt-1" data-testid="mp-duration-input" />
                <p className="text-[10px] text-[#8B7B73] mt-1">1h to 336h (14 days). Common: 24, 72, 168.</p>
              </div>
            </div>
          </div>
        )}

        <div>
          <Label htmlFor="mp-desc" className="text-xs">Description</Label>
          <Textarea id="mp-desc" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="mp-desc-input" className="rounded-sm mt-1" />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="mp-city" className="text-xs">City</Label>
            <Input id="mp-city" value={city} onChange={(e) => setCity(e.target.value)} data-testid="mp-city-input" className="rounded-sm mt-1" />
          </div>
          <div>
            <Label htmlFor="mp-state" className="text-xs">State</Label>
            <Input id="mp-state" value={state} onChange={(e) => setState(e.target.value)} data-testid="mp-state-input" className="rounded-sm mt-1" />
          </div>
          <div>
            <Label htmlFor="mp-zip" className="text-xs">ZIP</Label>
            <Input id="mp-zip" value={zip} onChange={(e) => setZip(e.target.value)} data-testid="mp-zip-input" className="rounded-sm mt-1" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Delivery</Label>
            <Select value={deliveryMode} onValueChange={setDeliveryMode}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="mp-delivery-input"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DELIVERY_OPTIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(deliveryMode === "shipping" || deliveryMode === "both") && (
            <div>
              <Label htmlFor="mp-shipping" className="text-xs">Shipping cost (USD)</Label>
              <Input id="mp-shipping" type="number" min={0} step="0.01" value={shippingDollars} onChange={(e) => setShippingDollars(e.target.value)} data-testid="mp-shipping-input" className="rounded-sm mt-1" />
            </div>
          )}
        </div>

        {/* Package dimensions — required when shipping is on so buyers can
            fetch live USPS/UPS rates from EasyPost (or the built-in estimator). */}
        {(deliveryMode === "shipping" || deliveryMode === "both") && (
          <div className="rounded-lg border border-[#EAE3DE] bg-[#FAF5F2] p-4 space-y-3" data-testid="mp-shipping-package-fields">
            <div className="text-xs font-mono uppercase tracking-wider text-[#8B7B73]">Package dimensions</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label htmlFor="mp-weight" className="text-xs">Weight (oz) <span className="text-[#B31E1E]">*</span></Label>
                <Input id="mp-weight" type="number" min={0} step="0.1" value={weightOz} onChange={(e) => setWeightOz(e.target.value)} placeholder="24" className="rounded-sm mt-1" data-testid="mp-weight-input" />
              </div>
              <div>
                <Label htmlFor="mp-length" className="text-xs">Length (in)</Label>
                <Input id="mp-length" type="number" min={0} step="0.1" value={lengthIn} onChange={(e) => setLengthIn(e.target.value)} placeholder="12" className="rounded-sm mt-1" data-testid="mp-length-input" />
              </div>
              <div>
                <Label htmlFor="mp-width" className="text-xs">Width (in)</Label>
                <Input id="mp-width" type="number" min={0} step="0.1" value={widthIn} onChange={(e) => setWidthIn(e.target.value)} placeholder="8" className="rounded-sm mt-1" data-testid="mp-width-input" />
              </div>
              <div>
                <Label htmlFor="mp-height" className="text-xs">Height (in)</Label>
                <Input id="mp-height" type="number" min={0} step="0.1" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} placeholder="6" className="rounded-sm mt-1" data-testid="mp-height-input" />
              </div>
            </div>
            <p className="text-[11px] text-[#8B7B73] leading-snug">
              Weight is required for live carrier rates. Dimensions improve accuracy.
            </p>
          </div>
        )}

        {/* Fixed-price only: Negotiable + Accepts offers */}
        {listingType === "fixed" && (
          <div className="space-y-2">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={negotiable} onChange={(e) => setNegotiable(e.target.checked)} data-testid="mp-negotiable-input" />
              Price is negotiable (OBO)
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={acceptsOffers} onChange={(e) => setAcceptsOffers(e.target.checked)} data-testid="mp-accepts-offers-input" className="mt-1" />
              <span>
                <span className="font-medium">Accept private offers</span>
                <span className="block text-[11px] text-[#8B7B73]">
                  Buyers can submit an offer below your asking price. You can accept, reject, or counter — no public bidding.
                </span>
              </span>
            </label>
          </div>
        )}

        {/* Artist / band merch — attach the listing to one of the user's
            approved artist profiles so it shows under "Merch" on that
            artist's public page. Only rendered when the user actually owns
            an approved artist profile. */}
        {myArtists.filter((a) => a.approved).length > 0 && (
          <div className="rounded-lg border border-[#EAE3DE] bg-[#FAF5F2] p-4 space-y-2" data-testid="mp-artist-merch-block">
            <div className="text-xs font-mono uppercase tracking-wider text-[#8B7B73]">Sell as artist merch</div>
            <Label className="text-xs" htmlFor="mp-artist-select">Attach to artist page (optional)</Label>
            <Select value={artistId || "none"} onValueChange={(v) => setArtistId(v === "none" ? "" : v)}>
              <SelectTrigger id="mp-artist-select" className="rounded-sm mt-1" data-testid="mp-artist-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Don&apos;t attach to any artist</SelectItem>
                {myArtists.filter((a) => a.approved).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.stage_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#8B7B73] leading-snug">
              Great for band tees, vinyl, prints, and other merch. This listing will appear in a &ldquo;Merch&rdquo; strip at the top of the selected artist&apos;s public page.
            </p>
          </div>
        )}

        {/* Live fee breakdown — reflects Stripe + Servrevu fees on the entered price. */}
        {(() => {
          const displayPrice = listingType === "auction" ? startingBidDollars : priceDollars;
          return parseFloat(displayPrice || "0") > 0 && (
            <FeeBreakdown
              priceDollars={displayPrice}
              shippingDollars={(deliveryMode === "shipping" || deliveryMode === "both") ? shippingDollars : 0}
              variant="full"
            />
          );
        })()}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="mp-submit-button">
            <Save size={13} className="mr-2" /> {saving ? "Saving…" : (isEdit ? "Save changes" : (listingType === "auction" ? "Start auction" : "Post listing"))}
          </Button>
        </div>
      </Card>
    </div>
  );
}
